// 찜질방 규칙과 NPC: 자리 차지, 문을 지나 구역 사이 걷기, 데크 계단, 탕 턱 넘기, 누워 구르기, 매점 주문·쟁반·먹기,
// NPC 판단, 내 카피바라 탭과 버튼 동작, 손님 29마리 배치. 프로토타입 3295–3536, 3184–3202, 3654–3664.
import * as THREE from 'three';
import { MENUS, type DishId } from '@capy/shared';
import { rand, rnd } from '../../engine/math';
import type { NavGrid } from '../../engine/nav';
import { part } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { makeAgent, setSize, type Capy, type EmoteKey } from '../../capy/agent';
import { SPEED, moveTo } from '../../capy/walker';
import { climbStep, hopDown, shakeOff } from '../../capy/stacking';
import { setFace, setHead } from '../../capy/equip';
import type { FurTone } from '../../capy/model';
import { serve } from '../../food/eating';
import { dish } from '../../food/dishes';
import { JJIM_TEXT as TX, fill } from '../../text/en';
import { JAREA, JDECK, JDOORS, PLAY, SHOP, TUBS, inPlay, inTub, jjimWet, type JArea, type Tub } from './layout';
import type { Spot } from './common';
import type { JjimScene } from './scene';
import { PHRASES } from '../lagoon/bots';

type V = THREE.Vector3;
export interface Route { area: JArea; pos: V; job: string }
export interface Tray { group: THREE.Group; keys: DishId[] }

export interface JjimCapy extends Capy {
  mode: 'idle' | 'route' | 'goto' | 'climb' | 'riding' | 'hopping';
  area: JArea;
  route: Route | null;
  wander: V | null;
  spot: Spot | null;
  spotPending: boolean;
  stayUntil: number;
  next: number;
  rideUntil?: number;
  /** 매점에 가기 전 자리 (쟁반을 들고 돌아갈 곳). */
  homeSpot?: Spot | null;
  /** 씻고 나서 들어갈 탕 자리. */
  after?: Spot | null;
  washed: boolean;
  /** 씻는 자리에서 씻는 중 (끝나면 washed). */
  jWash?: boolean;
  jhop?: { from: V; to: V; t0: number; dur: number; arc: number; then?: 'settle' | 'wade' } | null;
  /** 탕에 뛰어든 곳 (턱 바로 안쪽). 여기서 자리까지 물속을 걸어간다. */
  tubIn?: V | null;
  /** 탕 안에서 자리로 걸어가는 중. */
  wading?: boolean;
  stair?: { dir: 1 | -1; t0: number } | null;
  deckMove?: { to: V; then: 'spot' | 'down' } | null;
  freeLie?: boolean;
  sitUp?: boolean;
  slideT?: number;
  order?: { keys: DishId[]; readyAt: number } | null;
  tray?: Tray | null;
  foodQueue?: DishId[] | null;
  doorGuard?: boolean;
  dineView?: boolean;
  crumbT?: number;
}

export interface JjimRules {
  S: JjimScene;
  agents: JjimCapy[];
  world: THREE.Group;
  /** 프로토타입 전역 nav: 마지막으로 움직인 카피바라의 격자 (NPC가 뛰어내릴 자리를 찾을 때 쓴다). */
  navNow: NavGrid;
  /** setTimeout 대신 시계로 미룬 일. */
  later: { at: number; fn: (t: number) => void }[];
  say(a: Capy, text: string): void;
  /** 안내 문구 (내 카피바라). */
  note(text: string): void;
  /** 잠깐 뜨는 알림 (내 카피바라). */
  hint(text: string): void;
  /** 내 카피바라가 매점 주문 자리에 도착했다 (메뉴 열기). */
  onShop?(): void;
}

const navOf = (R: JjimRules, a: Capy) => ((a.size || 1) < 1 ? R.S.navBaby : R.S.nav);
const ignoreOf = (a: JjimCapy) => (o: Capy) => (o as JjimCapy).area !== a.area || ((o.elevY || 0) > 1) !== ((a.elevY || 0) > 1);
const speedOf = (a: Capy) => 1.8 * (1 - 0.4 * a.bathAmt);
const v3 = (p: { x: number; z: number }) => new THREE.Vector3(p.x, 0, p.z);
const doorOf = (area: JArea) => JDOORS.find((d) => d.area === area)!;
const nameOf = (area: JArea) => JAREA[area].name.toLowerCase();

// ---------- 자리, 길, 문 ----------
export function claimSpot(a: JjimCapy, s: Spot): void { releaseSpot(a); s.occ.push(a); a.spot = s; a.stayUntil = 0; }
export function releaseSpot(a: JjimCapy): void {
  if (a.spot) { const s = a.spot; s.occ = s.occ.filter((o) => o !== a); if (s.stream && !s.occ.length) s.stream.visible = false; }
  a.spot = null; a.slideT = 0; a.bounceY = 0;
}
function spotOffset(s: Spot, a: JjimCapy): V {
  if (s.cap <= 1 || s.kind === 'order') return s.pos;
  const i = s.occ.indexOf(a);
  return s.pos.clone().add(new THREE.Vector3((i - (s.occ.length - 1) / 2) * 1.1, 0, 0));
}
export function routeTo(a: JjimCapy, area: JArea, pos: { x: number; z: number }, job: string): void {
  a.route = { area, pos: v3(pos), job }; a.mode = 'route'; a.path = null; a.wander = null; a.lying = false; a.freeLie = false;
}
export function goSpot(a: JjimCapy, s: Spot, then?: string): void {
  releaseSpot(a); claimSpot(a, s); a.spotPending = true;
  if (s.deck) { if ((a.elevY || 0) > 1) { a.mode = 'idle'; a.route = null; a.deckMove = { to: s.pos.clone(), then: 'spot' }; } else routeTo(a, 'hall', JDECK.stairBot, 'deckUp'); return; }
  const T = tubOf(s);
  if (T) {
    // 이미 그 탕 안이면 물속으로 바로 걸어가고, 아니면 지금 자리(다른 방이면 목욕탕 문)에서 가장 가까운 턱으로 가서 뛰어든다
    if (inTub(T, a.pos.x, a.pos.z, 0)) { a.mode = 'idle'; a.route = null; a.tubIn = null; a.wading = true; return; }
    const from = a.area === 'bath' ? a.pos : doorOf('bath').roomIn, e = tubEntry(T, from, s.pos);
    a.tubIn = e.inside; routeTo(a, s.area, e.outside, then || 'spot'); return;
  }
  routeTo(a, s.area, s.approach || s.pos, then || 'spot');
}
const tubOf = (s: Spot) => (s.pose === 'soak' ? TUBS[s.kind as 'hot' | 'cold'] : null);
/** 탕에 들어갈 곳: 네 변 중 (from에서 턱 앞까지 걷는 거리 + 턱 안쪽에서 자리까지 물속 거리)가 가장 짧은 곳.
 *  outside: 턱 앞에 설 자리, inside: 뛰어들 턱 바로 안쪽. */
function tubEntry(T: Tub, from: { x: number; z: number }, seat: { x: number; z: number }): { outside: V; inside: V } {
  const cl = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  // 변마다 from과 자리의 중간쯤에서 들어간다
  const mx = cl((from.x + seat.x) / 2, T.x0 + 0.7, T.x1 - 0.7), mz = cl((from.z + seat.z) / 2, T.z0 + 0.7, T.z1 - 0.7);
  const sides: [V, V][] = [
    [new THREE.Vector3(T.x0 - 0.85, 0, mz), new THREE.Vector3(T.x0 + 0.7, 0, mz)], [new THREE.Vector3(T.x1 + 0.85, 0, mz), new THREE.Vector3(T.x1 - 0.7, 0, mz)],
    [new THREE.Vector3(mx, 0, T.z0 - 0.85), new THREE.Vector3(mx, 0, T.z0 + 0.7)], [new THREE.Vector3(mx, 0, T.z1 + 0.85), new THREE.Vector3(mx, 0, T.z1 - 0.7)],
  ];
  const cost = ([o, i]: [V, V]) => Math.hypot(o.x - from.x, o.z - from.z) + Math.hypot(i.x - seat.x, i.z - seat.z);
  const best = sides.reduce((m, sd) => (cost(sd) < cost(m) ? sd : m));
  return { outside: best[0], inside: best[1] };
}
function teleport(R: JjimRules, a: JjimCapy, area: JArea, pos: { x: number; z: number }, face: number) {
  a.area = area; a.pos.set(pos.x, 0, pos.z); a.c.root.position.copy(a.pos); a.path = null; a.facing = a.targetFacing = face; a.bathAmt = 0; a.doorGuard = true;
  if (area === 'bath') a.washed = false;
  for (let r = a.rider as JjimCapy | null | undefined; r; r = r.rider as JjimCapy | null | undefined) r.area = area;
  if (a.isPlayer) {
    R.note(area === 'hall' ? TX.backInHall : fill(TX.walkedInto, { name: nameOf(area), ko: JAREA[area].ko }) + (area === 'bath' ? TX.bathRule : ''));
    if (area === 'bath') R.hint(TX.washFirstHint);
  }
}
function routeTarget(a: JjimCapy): { x: number; z: number } | null {
  const Rt = a.route;
  if (!Rt) return null;
  if (a.area === Rt.area) return Rt.pos;
  if (a.area !== 'hall') return doorOf(a.area).roomP;
  return doorOf(Rt.area).hallP;
}

/** 찜질방에서 한 프레임 이동. 움직였으면 true. 프로토타입 jjimStep 3316–3377. */
export function jjimStep(R: JjimRules, a: JjimCapy, t: number, dt: number): boolean {
  if (a.riding || a.hop || a.mode === 'riding' || a.mode === 'hopping') { a.bathAmt = 0; a.lying = false; return false; }
  const nav = R.navNow = navOf(R, a);
  if (a.mode === 'climb') { releaseSpot(a); a.lying = false; return climbStep(a, t, dt, nav, R.agents, { ignore: ignoreOf(a), speedK: speedOf(a), wet: jjimWet }); }
  let moving = false;
  // 탕 턱을 폴짝 넘기
  if (a.jhop) {
    const H = a.jhop, p = Math.min(1, (t - H.t0) / H.dur);
    a.pos.lerpVectors(H.from, H.to, p); a.bounceY = Math.sin(p * Math.PI) * H.arc; a.targetFacing = Math.atan2(H.to.x - H.from.x, H.to.z - H.from.z);
    a.bathAmt += (jjimWet(a.pos.x, a.pos.z) - a.bathAmt) * Math.min(1, dt * 3);
    if (p >= 1) { a.jhop = null; a.bounceY = 0; if (H.then === 'settle') settleTub(R, a); else if (H.then === 'wade') a.wading = true; }
    return true;
  }
  // 탕 안에서 자리까지 물을 헤치며 걷기 (턱에 막히지 않게 격자 대신 곧장)
  if (a.wading && a.spot) {
    const P = spotOffset(a.spot, a), dx = P.x - a.pos.x, dz = P.z - a.pos.z, d = Math.hypot(dx, dz), st = Math.min(d, SPEED * 0.9 * dt);
    a.bathAmt += (jjimWet(a.pos.x, a.pos.z) - a.bathAmt) * Math.min(1, dt * 3);
    if (d > 0.05) { a.pos.x += dx / d * st; a.pos.z += dz / d * st; a.targetFacing = Math.atan2(dx, dz); return true; }
    a.wading = false; settleTub(R, a); return false;
  }
  if (a.wading) a.wading = false;
  // 데크 계단 오르내리기
  if (a.stair) {
    const S2 = a.stair, p = Math.min(1, (t - S2.t0) / 1.8), q = S2.dir > 0 ? p : 1 - p;
    a.pos.lerpVectors(v3(JDECK.stairBot), v3(JDECK.stairTop), q); a.elevY = JDECK.H * q + Math.abs(Math.sin(q * Math.PI * 7)) * 0.06; a.targetFacing = S2.dir > 0 ? 0 : Math.PI;
    if (p >= 1) { a.stair = null; a.elevY = S2.dir > 0 ? JDECK.H : 0; if (S2.dir > 0) { const sp = a.spot && a.spot.deck ? a.spot : null; a.deckMove = sp ? { to: sp.pos.clone(), then: 'spot' } : null; if (!sp) a.mode = 'idle'; } }
    return true;
  }
  if (a.deckMove) {
    const M = a.deckMove, dx = M.to.x - a.pos.x, dz = M.to.z - a.pos.z, d = Math.hypot(dx, dz), st = Math.min(d, SPEED * 1.6 * dt);
    if (d > 0.05) { a.pos.x += dx / d * st; a.pos.z += dz / d * st; a.targetFacing = Math.atan2(dx, dz); return true; }
    a.deckMove = null;
    if (M.then === 'spot') { a.spotPending = false; if (a.isPlayer) R.note(TX.onDeck); }
    else if (M.then === 'down') a.stair = { dir: -1, t0: t };
    return true;
  }
  // 어디 가기 전에 데크나 탕에서 먼저 나온다
  if ((a.elevY || 0) > 1 && (a.mode === 'route' || a.mode === 'goto')) { a.deckMove = { to: v3(JDECK.stairTop), then: 'down' }; return true; }
  // (다른 탕으로 옮길 때도 먼저 나온다)
  if ((a.mode === 'route' || a.mode === 'goto') && a.bathAmt > 0.3) {
    for (const T of [TUBS.hot, TUBS.cold]) {
      if (!inTub(T, a.pos.x, a.pos.z, 0)) continue;
      const opts: [number, number][] = [[a.pos.x, T.z0 - 0.85], [a.pos.x, T.z1 + 0.85], [T.x0 - 0.85, a.pos.z], [T.x1 + 0.85, a.pos.z]], tg2 = a.mode === 'route' ? routeTarget(a) : a.wander;
      const tx = tg2 ? tg2.x : a.pos.x, tz = tg2 ? tg2.z : a.pos.z;
      opts.sort((p2, q2) => Math.hypot(p2[0] - tx, p2[1] - tz) - Math.hypot(q2[0] - tx, q2[1] - tz));
      a.jhop = { from: a.pos.clone(), to: new THREE.Vector3(opts[0]![0], 0, opts[0]![1]), t0: t, dur: 0.8, arc: 0.9 };
      return true;
    }
  }
  // 누운 채 구르기
  if (a.rollAnim) {
    const Ra = a.rollAnim, p = Math.min(1, (t - Ra.t0) / Ra.dur), e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
    a.roll = -Ra.dir * Math.PI * 2 * e;
    const lat = Ra.dir * Ra.dist * e, nx = Ra.px + Math.cos(a.facing) * lat, nz = Ra.pz - Math.sin(a.facing) * lat;
    if (!nav.blockedAt(nx, nz)) { a.pos.x = nx; a.pos.z = nz; } else if (p < 0.98) { Ra.dur = Math.max(0.2, t - Ra.t0); }
    if (p >= 1) { a.rollAnim = null; a.roll = 0; if (a.spot && Math.hypot(a.pos.x - a.spot.pos.x, a.pos.z - a.spot.pos.z) > 0.5) { releaseSpot(a); a.freeLie = true; } }
  }
  if (a.mode === 'route' || a.mode === 'goto') {
    const tg = a.mode === 'route' ? routeTarget(a) : a.wander;
    if (tg) {
      const dist = Math.hypot(tg.x - a.pos.x, tg.z - a.pos.z), same = a.mode !== 'route' || a.area === a.route!.area;
      moving = dist > 0.1 && moveTo(a, tg, dt, t, nav, R.agents, same && dist < 1.6, ignoreOf(a), speedOf(a));
      // 문 앞까지 걸어가면 건너편 방으로
      if (a.mode === 'route' && a.area !== a.route!.area) {
        const d = a.area === 'hall' ? doorOf(a.route!.area) : doorOf(a.area), P = a.area === 'hall' ? d.hallP : d.roomP;
        if (Math.hypot(a.pos.x - P.x, a.pos.z - P.z) < 0.75) { if (a.area === 'hall') teleport(R, a, d.area, d.roomIn, Math.PI); else teleport(R, a, 'hall', d.hallIn, 0); moving = true; }
      } else if (!moving) arrive(R, a, t);
    } else a.mode = 'idle';
  }
  // 내 카피바라가 문 가까이 걸어가면 건너편 방으로
  if (a.isPlayer && a.mode !== 'route' && !a.rollAnim) {
    const d = a.area === 'hall' ? JDOORS.find((q) => Math.hypot(a.pos.x - q.hallP.x, a.pos.z - q.hallP.z) < 0.75) : (() => { const q = JDOORS.find((q2) => q2.area === a.area); return q && Math.hypot(a.pos.x - q.roomP.x, a.pos.z - q.roomP.z) < 0.75 ? q : undefined; })();
    if (d && !a.doorGuard) { releaseSpot(a); a.mode = 'idle'; if (a.area === 'hall') teleport(R, a, d.area, d.roomIn, Math.PI); else teleport(R, a, 'hall', d.hallIn, 0); }
    if (a.doorGuard && !d) a.doorGuard = false;
  } else if (!a.isPlayer && a.doorGuard) {
    const near = JDOORS.some((q) => Math.hypot(a.pos.x - q.hallP.x, a.pos.z - q.hallP.z) < 1.2 || Math.hypot(a.pos.x - q.roomP.x, a.pos.z - q.roomP.z) < 1.2);
    if (!near) a.doorGuard = false;
  }
  // 자리에 닿았으면 살살 맞추고 방향을 돌린다
  const s = a.spot;
  if (s && !a.spotPending && !moving && !a.rollAnim) {
    const P = spotOffset(s, a), k = Math.min(1, dt * 3); a.pos.x += (P.x - a.pos.x) * k; a.pos.z += (P.z - a.pos.z) * k;
    if (s.pose !== 'pit' && s.pose !== 'tramp') a.targetFacing = s.face;
  }
  if (!moving && !s && !a.rollAnim) {
    let sx = 0, sz = 0;
    for (const o of R.agents) {
      if (o === a || o.riding || o.hop || o.area !== a.area) continue;
      const ox = a.pos.x - o.pos.x, oz = a.pos.z - o.pos.z, od = Math.hypot(ox, oz);
      if (od < 1.4 && od > 0.001) { const w = (1.4 - od) / 1.4; sx += ox / od * w; sz += oz / od * w; }
    }
    if (Math.hypot(sx, sz) > 0.05) { const nx = a.pos.x + sx * dt * 0.35, nz = a.pos.z + sz * dt * 0.35; if (!nav.blockedAt(nx, nz)) { a.pos.x = nx; a.pos.z = nz; } }
  }
  a.bathAmt += (jjimWet(a.pos.x, a.pos.z) - a.bathAmt) * Math.min(1, dt * 0.9);
  a.lying = !a.eating && !a.tray && !a.order && (!!(s && !a.spotPending && s.pose === 'lie' && !a.sitUp) || !!(a.freeLie && !moving));
  return moving;
}

function settleTub(R: JjimRules, a: JjimCapy) {
  a.spotPending = false;
  if (a.isPlayer) R.note(a.spot && a.spot.kind === 'hot' ? TX.hotTub : TX.coldTub);
}

function arrive(R: JjimRules, a: JjimCapy, t: number) {
  const Rt = a.route; a.mode = 'idle'; a.route = null; a.wander = null;
  if (Rt && Rt.job === 'deckUp') { a.stair = { dir: 1, t0: t }; return; }
  if (a.spot && a.spotPending && a.spot.approach) { a.jhop = { from: a.pos.clone(), to: a.tubIn ? a.tubIn.clone() : a.spot.pos.clone(), t0: t, dur: 0.8, arc: 0.9, then: a.tubIn ? 'wade' : 'settle' }; a.tubIn = null; return; }
  if (a.spot && a.spotPending) {
    a.spotPending = false; const s = a.spot;
    if (s.kind === 'order') { if (a.isPlayer) R.onShop?.(); else { const keys = Object.keys(MENUS.jjimshop) as DishId[]; placeOrder(R, a, keys[Math.floor(Math.random() * keys.length)]!, t); } }
    if (s.pose === 'wash' || s.pose === 'shower') { a.washT = t; a.jWash = true; }
    if (s.pose === 'slide') a.slideT = t;
    if (a.tray) { const tr = a.tray; a.tray = null; tr.group.parent?.remove(tr.group); a.sitUp = true; eatHere(R, a, tr.keys, t, s.table); }
    if (a.isPlayer && s.pose === 'shower') R.note(TX.showerOn);
    if (a.isPlayer && s.pose === 'wash') R.note(TX.dipper);
    return;
  }
  if (Rt && Rt.job === 'eatHere' && a.tray) { const tr = a.tray; a.tray = null; tr.group.parent?.remove(tr.group); eatHere(R, a, tr.keys, t); }
}

// ---------- 매점: 주문 → 3초 → 쟁반 들고 자리로 → 먹기 ----------
export function placeOrder(R: JjimRules, a: JjimCapy, keys: DishId | DishId[], t: number): void {
  a.order = { keys: Array.isArray(keys) ? keys : [keys], readyAt: t + 3 };
  if (a.isPlayer) R.note(fill(TX.cooking, { n: 3 }));
}
let lastCount = 0;
export function orderTick(R: JjimRules, a: JjimCapy, t: number): void {
  const o = a.order;
  if (!o) return;
  if (a.isPlayer) { const left = Math.ceil(o.readyAt - t); if (left > 0 && left !== lastCount) { lastCount = left; R.note(fill(TX.cooking, { n: left })); } }
  if (t < o.readyAt) return;
  a.order = null; lastCount = 0;
  const n = o.keys.length, tray = new THREE.Group();
  tray.add(part(new THREE.BoxGeometry(n > 1 ? 1.15 : 0.85, 0.04, n > 2 ? 0.95 : 0.6), plush('#B5763E', '#D9A066', 0.05)));
  o.keys.forEach((k, i) => { const d = dish(k); d.group.position.set(n > 1 ? (i % 2 ? 0.27 : -0.27) : 0, 0.02, n > 2 ? (i < 2 ? -0.22 : 0.22) : 0); d.group.scale.setScalar(n > 1 ? 0.44 : 0.62); tray.add(d.group); });
  R.world.add(tray);
  a.tray = { group: tray, keys: o.keys };
  const S = R.S.spots;
  let home: Spot | null = S.filter((s2) => s2.kind === 'dine' && s2.occ.length < s2.cap).sort((p2, q2) => p2.pos.distanceTo(a.pos) - q2.pos.distanceTo(a.pos))[0] || null;
  if (!home) home = a.homeSpot && a.homeSpot.occ.length < a.homeSpot.cap && !a.homeSpot.baby && !a.homeSpot.deck ? a.homeSpot : null;
  if (!home) { const cands = S.filter((s) => s.area === 'hall' && (s.kind === 'mat' || s.kind === 'log') && s.occ.length < s.cap); cands.sort((p, q) => p.pos.distanceTo(a.pos) - q.pos.distanceTo(a.pos)); home = cands[0] || null; }
  if (home) goSpot(a, home); else routeTo(a, 'hall', { x: SHOP.order.x, z: SHOP.order.z + 2.5 }, 'eatHere');
  if (a.isPlayer) R.note(home && home.kind === 'dine' ? TX.toTable : TX.toSpot);
}
const vT = new THREE.Vector3(), vT2 = new THREE.Vector3();
/** 쟁반을 두 앞발 사이 앞쪽에 맞춘다. */
export function placeTray(a: JjimCapy): void {
  const c = a.c; c.root.updateMatrixWorld(true); c.paws[0]!.getWorldPosition(vT); c.paws[1]!.getWorldPosition(vT2); vT.add(vT2).multiplyScalar(0.5);
  vT.x += Math.sin(a.facing) * 0.25; vT.z += Math.cos(a.facing) * 0.25; vT.y -= 0.06;
  a.tray!.group.position.copy(vT); a.tray!.group.rotation.set(0, a.facing, 0); a.tray!.group.scale.setScalar(a.size || 1);
}
function eatHere(R: JjimRules, a: JjimCapy, keysIn: DishId[], t: number, table?: V) {
  const keys = keysIn.slice(), key = keys.shift()!; a.foodQueue = keys;
  const f = new THREE.Vector3(Math.sin(a.facing), 0, Math.cos(a.facing)), spot = table ? table.clone() : a.pos.clone().addScaledVector(f, 1.35 * (a.size || 1));
  if (!table) spot.y = Math.max(0, (a.spot?.seatY || 0) - 0.05);
  const d = serve(a, key, spot, R.world, t);
  a.eating!.done = () => {
    if (a.isPlayer) R.note(TX.allGone);
    const t2 = nowOf(R);
    if (key === 'buldak') { a.spicyUntil = t2 + 9; if (!a.isPlayer && Math.random() < 0.6) R.say(a, 'ayy'); if (a.isPlayer) R.note(TX.spicy); }
    R.later.push({ at: t2 + (keys.length ? 0.9 : 2.4), fn: (t3) => {
      d.group.parent?.remove(d.group); if (a.dish === d) a.dish = null;
      if (a.foodQueue && a.foodQueue.length && a.spot && !a.spotPending) { if (a.isPlayer) R.note(TX.nextUp); eatHere(R, a, a.foodQueue, t3, table); return; }
      a.foodQueue = null; a.sitUp = false; if (a.isPlayer) R.note(TX.allGoneGetUp);
    } });
  };
}
/** 방 시계 (update가 매 프레임 적는다). */
const clockOf = new WeakMap<JjimRules, number>();
export const setNow = (R: JjimRules, t: number): void => { clockOf.set(R, t); };
const nowOf = (R: JjimRules) => clockOf.get(R) ?? 0;

// ---------- NPC 판단 ----------
const EMOTES: EmoteKey[] = ['wave', 'wobble', 'yawn'];
/** 프로토타입 jjimThink 3451–3479. */
export function jjimThink(R: JjimRules, a: JjimCapy, t: number): void {
  if (a.isPlayer || a.mode === 'climb' || a.mode === 'route' || a.eating || a.order || a.tray || a.rollAnim || a.washT) return;
  if (a.mode === 'riding' || a.mode === 'hopping') {
    if (a.mode === 'riding' && a.riding && t > (a.rideUntil || 0)) hopDown(a, t, R.navNow, (x) => { (x as JjimCapy).mode = 'idle'; });
    a.next = t + 2; return;
  }
  if (t < a.next) return;
  if (a.spot && t < a.stayUntil) {
    if (a.lying && !a.spot.deck && Math.random() < 0.12) startRollJ(R, a, Math.random() < 0.5 ? -1 : 1, t);
    else if (Math.random() < 0.08) R.say(a, PHRASES[Math.floor(Math.random() * PHRASES.length)]!);
    a.next = t + rand(4, 9); return;
  }
  // 씻었으면 가려던 탕으로
  if (a.washed && a.after) { const s = a.after; a.after = null; if (s.occ.length < s.cap) { goSpot(a, s); a.stayUntil = t + 999; return; } }
  const pick = (filter: (s: Spot) => boolean) => { const c = R.S.spots.filter((s) => filter(s) && s.kind !== 'dine' && s.occ.length < s.cap && (!s.baby || a.size < 1)); return c.length ? c[Math.floor(Math.random() * c.length)]! : null; };
  a.freeLie = false; a.sitUp = false;
  let s: Spot | null = null; const r = Math.random();
  if (a.size < 1 && r < 0.45) s = pick((q) => !!q.baby);
  else if (r < 0.22) s = pick((q) => q.area === 'hall' && q.kind === 'mat');
  else if (r < 0.32) s = pick((q) => q.kind === 'log');
  else if (r < 0.37) s = pick((q) => q.kind === 'cubby');
  else if (r < 0.42) s = pick((q) => q.kind === 'deck');
  else if (r < 0.5) s = pick((q) => q.area === 'hwangto');
  else if (r < 0.6) s = pick((q) => q.area === 'salt');
  else if (r < 0.66) s = pick((q) => q.area === 'ice');
  else if (r < 0.78) { const tub = pick((q) => q.pose === 'soak'); if (tub) { if (a.washed && a.area === 'bath') s = tub; else { const w = pick((q) => q.pose === 'wash' || q.pose === 'shower'); if (w) { s = w; a.after = tub; } } } }
  else if (r < 0.85 && !a.dish) s = pick((q) => q.kind === 'order');
  else if (r < 0.92) { R.say(a, PHRASES[Math.floor(Math.random() * PHRASES.length)]!); a.next = t + rand(3, 8); return; }
  else { a.emote = EMOTES[Math.floor(Math.random() * 3)]!; a.emoteStart = t; a.next = t + rand(4, 8); return; }
  if (s) { if (s.kind === 'order') a.homeSpot = a.spot; goSpot(a, s); a.stayUntil = t + (s.pose === 'soak' ? rand(18, 40) : s.pose === 'wash' || s.pose === 'shower' ? 0 : s.baby ? rand(10, 25) : s.area === 'ice' ? rand(10, 20) : rand(20, 55)); }
  a.next = t + rand(3, 8);
}

/** 누운 채 옆으로 구르기 (2층 데크에서는 안 된다). */
export function startRollJ(R: JjimRules, a: JjimCapy, dir: -1 | 1, t: number): void {
  if (!a.lying || a.rollAnim || (a.elevY || 0) > 1) { if (a.isPlayer && (a.elevY || 0) > 1) R.hint(TX.noRollDeck); return; }
  a.rollAnim = { t0: t, dur: 1.5, dir, px: a.pos.x, pz: a.pos.z, dist: 2.4 * (a.size || 1) };
  if (a.isPlayer) a.freeLie = true;
}

// ---------- 내 카피바라: 탭과 버튼 ----------
/** 바닥을 탭했을 때: 문, 매점, 미끄럼틀, 계단, 놀이터, 가까운 자리, 아니면 그 자리로. 걸어갈 곳이면 true (탭 표시). 프로토타입 jjimTap 3483–3509. */
export function jjimTap(R: JjimRules, a: JjimCapy, hx: number, hz: number): boolean {
  a.napping = false; a.emote = null; a.sitUp = false;
  if (a.order || a.tray) { R.hint(TX.foodComing); return false; }
  if (a.eating) { R.hint(TX.finishFood); return false; }
  const area = a.area;
  if (area === 'hall') { const d = JDOORS.find((q) => Math.abs(hx - q.hx) < 1.8 && hz < JAREA.hall.z0 + 2.2); if (d) { goRoom(R, a, d.area); return false; } }
  else { const d = doorOf(area); if (Math.hypot(hx - d.roomP.x, hz - d.roomP.z) < 2.0) { goRoom(R, a, 'hall'); return false; } }
  if (area === 'hall' && hx > 12 && hx < 22 && hz > JAREA.hall.z0 && hz < JAREA.hall.z0 + 3.2) { goToShop(R, a); return false; }
  if (area === 'hall' && a.size < 1 && Math.abs(hx - PLAY.slide.x) < 0.9 && hz > PLAY.slide.z0 - 1.2 && hz < PLAY.slide.z1 + 0.5) { playAt(R, a, 'slide'); return false; }
  if (area === 'hall' && hx > JDECK.x0 - 0.5 && hx < JDECK.x1 && hz > JDECK.stairBot.z - 0.6 && hz < JDECK.z0) { goDeck(R, a); return false; }
  if (area === 'hall' && inPlay(hx, hz) && a.size >= 1) { R.hint(TX.babiesOnly); R.note(TX.babiesOnlyNote); releaseSpot(a); a.freeLie = false; routeTo(a, 'hall', PLAY.gate, 'go'); return false; }
  let best: Spot | null = null, bd = 1.5;
  for (const s of R.S.spots) {
    if (s.area !== area || s.kind === 'order' || s.deck) continue;
    const sp = s.pose === 'lie' ? s.pos.clone().addScaledVector(new THREE.Vector3(-Math.sin(s.face), 0, -Math.cos(s.face)), 1.0) : s.pos, d = Math.hypot(hx - sp.x, hz - sp.z);
    if (d < bd && (s.occ.length < s.cap || s.occ.includes(a))) { bd = d; best = s; }
  }
  if (best && best.baby && a.size >= 1) best = null;
  // 탕 안 아무 데나 탭: 그 탕에서 탭한 곳에 가장 가까운 빈자리로 (자리 근처를 탭하면 위에서 그 자리). 빈자리가 없으면 알려 준다
  if (!best && area === 'bath') {
    for (const k of ['hot', 'cold'] as const) {
      if (!inTub(TUBS[k], hx, hz, -0.5)) continue;
      const free = R.S.spots.filter((q) => q.kind === k && (q.occ.length < q.cap || q.occ.includes(a)));
      if (!free.length) { R.hint(fill(TX.tubFullTap, { tub: k })); return false; }
      best = free.sort((p2, q2) => Math.hypot(p2.x - hx, p2.z - hz) - Math.hypot(q2.x - hx, q2.z - hz))[0]!;
    }
  }
  if (best) {
    a.freeLie = false;
    if (best.pose === 'soak' && !a.washed) {
      const w = nearest(R, a, (q) => (q.pose === 'wash' || q.pose === 'shower') && q.occ.length < q.cap);
      if (w) { a.after = best; goSpot(a, w); R.note(TX.washFirstNote); R.hint(TX.washFirst); return false; }
    }
    a.homeSpot = best; goSpot(a, best); return false;
  }
  releaseSpot(a); a.freeLie = false; a.mode = 'goto'; a.wander = new THREE.Vector3(hx, 0, hz);
  return true;
}
const nearest = (R: JjimRules, a: JjimCapy, f: (s: Spot) => boolean) => R.S.spots.filter(f).sort((p2, q2) => p2.pos.distanceTo(a.pos) - q2.pos.distanceTo(a.pos))[0] || null;

/** 다른 구역으로 (문을 지나 걸어간다). */
export function goRoom(R: JjimRules, a: JjimCapy, area: JArea): void {
  if (a.order || a.tray) { R.hint(TX.waitFood); return; }
  if (a.eating) { R.hint(TX.finishFood); return; }
  releaseSpot(a); a.freeLie = false; a.sitUp = false;
  if (area === a.area) { const m = fill(TX.already, { name: nameOf(area) }); R.note(m); R.hint(m); return; }
  const d = doorOf(area === 'hall' ? a.area : area);
  routeTo(a, area, area === 'hall' ? { x: d.hallIn.x, z: d.hallIn.z + 1.5 } : { x: d.roomIn.x, z: d.roomIn.z - 1.5 }, 'enter');
  R.note(area === 'hall' ? TX.toHall : fill(TX.toRoom, { name: nameOf(area), ko: JAREA[area].ko }));
}
export function goDeck(R: JjimRules, a: JjimCapy): void {
  const s = R.S.spots.filter((q) => q.deck && q.occ.length < q.cap)[0];
  if (!s) { R.hint(TX.deckFull); return; }
  a.freeLie = false; a.sitUp = false; goSpot(a, s); R.note(TX.toDeck);
}
/** 데크 위를 탭: 가장 가까운 데크 자리로. */
export function deckTap(R: JjimRules, a: JjimCapy, x: number, z: number): void {
  const s = R.S.spots.filter((q) => q.deck && (q.occ.length < q.cap || q.occ.includes(a))).sort((p, q) => Math.hypot(p.pos.x - x, p.pos.z - z) - Math.hypot(q.pos.x - x, q.pos.z - z))[0];
  if (!s) { R.hint(TX.deckFull); return; }
  a.freeLie = false; a.sitUp = false; const up = (a.elevY || 0) > 1; goSpot(a, s); R.note(up ? TX.alongDeck : TX.toDeck);
}
export type BathJob = 'wash' | 'shower' | 'hot' | 'cold';
export function bathDo(R: JjimRules, a: JjimCapy, kind: BathJob): void {
  if (a.area !== 'bath') { goRoom(R, a, 'bath'); return; }
  a.freeLie = false; a.sitUp = false;
  if (kind === 'wash' || kind === 'shower') {
    const s = nearest(R, a, (q) => q.pose === kind && q.occ.length < q.cap);
    if (!s) { R.hint(TX.allTaken); return; }
    a.after = null; goSpot(a, s); R.note(kind === 'wash' ? TX.toWash : TX.toShower); return;
  }
  const tub = nearest(R, a, (q) => q.kind === kind && q.occ.length < q.cap);
  if (!tub) { R.hint(fill(TX.tubFullTap, { tub: kind })); return; }
  if (!a.washed) { const w = nearest(R, a, (q) => (q.pose === 'wash' || q.pose === 'shower') && q.occ.length < q.cap); if (w) { a.after = tub; goSpot(a, w); R.note(fill(TX.washThenTub, { tub: kind })); R.hint(TX.washFirst); return; } }
  goSpot(a, tub); R.note(fill(TX.intoTub, { tub: kind }));
}
export type PlayKind = 'slide' | 'tramp' | 'pit';
export function playAt(R: JjimRules, a: JjimCapy, kind: PlayKind): void {
  if (a.size >= 1) { R.hint(TX.babiesOnly); R.note(TX.babiesOnlyNote); return; }
  if (a.area !== 'hall') { goRoom(R, a, 'hall'); return; }
  const s = R.S.spots.filter((q) => q.kind === kind && (q.occ.length < q.cap || q.occ.includes(a)))[0];
  if (!s) { R.hint(TX.taken); return; }
  a.freeLie = false; a.sitUp = false; goSpot(a, s); R.note(TX.play[kind]);
}
export function goToShop(R: JjimRules, a: JjimCapy): void {
  if (a.order || a.tray) { R.hint(TX.orderOnWay); return; }
  if (a.eating) { R.hint(TX.finishFood); return; }
  a.homeSpot = a.spot && a.spot.kind !== 'order' ? a.spot : a.homeSpot; a.freeLie = false; a.sitUp = false;
  goSpot(a, R.S.spots.find((q) => q.kind === 'order')!); R.note(TX.toShop);
}
/** 눕기 ↔ 일어나기. 프로토타입 toggleLie 3654–3661. */
export function toggleLieJ(R: JjimRules, a: JjimCapy, t: number): void {
  if (a.order || a.tray || a.eating) { R.hint(TX.lieAfterEat); return; }
  if (!a.lying && a.bathAmt >= 0.2) { R.hint(TX.noLieWater); return; }
  if (a.lying) { if (a.spot && a.spot.pose === 'lie') a.sitUp = true; a.freeLie = false; }
  else if (a.spot && a.spot.pose === 'lie' && !a.spotPending) a.sitUp = false;
  else if (a.bathAmt < 0.2 && a.mode !== 'route' && (a.elevY || 0) < 1 && !(a.spot && a.spot.baby)) { if (a.spot) releaseSpot(a); a.mode = 'idle'; a.freeLie = true; if (a.rider) shakeOff(a, t, navOf(R, a)); }
}

// ---------- 손님 배치 ----------
export function asJjim(a: Capy): JjimCapy {
  return Object.assign(a, { mode: 'idle', area: 'hall', route: null, wander: null, spot: null, spotPending: false, stayUntil: 0, next: 0, washed: false } as const) as unknown as JjimCapy;
}
/** 손님 29마리: 홀 13, 황토방 4, 소금방 4, 얼음방 2, 목욕탕 6. 프로토타입 3184–3202. */
export function populate(R: JjimRules): void {
  const tones: FurTone[] = ['classic', 'honey', 'cocoa'];
  const hpool = ['yangmeori', 'yangmeori', 'yangmeori', 'yangmeori', 'stoneegg', 'bananamilk', 'bagaji', 'none', 'orange', 'duckling', 'frog'] as const;
  const plan: [JArea, number][] = [['hall', 13], ['hwangto', 4], ['salt', 4], ['ice', 2], ['bath', 6]];
  let idx = 0;
  plan.forEach(([area, n]) => {
    for (let k = 0; k < n; k++, idx++) {
      const a = asJjim(makeAgent(R.world, false, tones[(idx + 2) % 3]!));
      setHead(a, hpool[Math.floor(rnd(idx + 7000) * hpool.length)]!);
      R.agents.push(a);
      if (idx % 9 === 5) setSize(a, 0.5);
      const fr2 = rnd(idx + 7100); if (fr2 < 0.13) setFace(a, 'cucumber'); else if (fr2 < 0.24) setFace(a, 'kbeauty');
      a.area = area; a.washed = area === 'bath';
      const free = R.S.spots.filter((s) => s.area === area && s.occ.length < s.cap && !s.baby && s.kind !== 'order' && s.kind !== 'wash' && s.kind !== 'shower' && s.kind !== 'dine');
      const s = free[Math.floor(rnd(idx + 7200) * free.length)];
      if (s && !(a.size < 1 && area === 'hall' && rnd(idx) < 0.6)) { claimSpot(a, s); a.pos.copy(s.pos); a.facing = a.targetFacing = s.face; a.stayUntil = rand(10, 40); if (s.pose === 'soak') a.bathAmt = 1; if (s.deck) a.elevY = JDECK.H; }
      else {
        const A = JAREA[area]; a.pos.set(rand(A.x0 + 2, A.x1 - 2), 0, rand(A.z0 + 2, A.z1 - 2));
        if (a.size < 1) { const ks = R.S.spots.filter((q) => q.baby && q.occ.length < q.cap); if (ks.length) { const q = ks[k % ks.length]!; claimSpot(a, q); a.pos.copy(q.pos); a.stayUntil = rand(8, 20); } }
      }
      a.c.root.position.copy(a.pos); a.next = rand(1, 12);
    }
  });
}
